<template>
  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
    <!-- Total Monthly Cost -->
    <div class="glass-panel p-6 bg-gradient-to-br from-purple-500/10 to-blue-500/10 hover:shadow-xl transition-shadow">
      <div class="flex justify-between items-start mb-2">
        <div class="text-gray-500 dark:text-gray-400 text-sm font-medium">Total Monthly Cost</div>
        <div class="p-2 bg-purple-500/10 rounded-lg">
          <svg class="w-5 h-5 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </div>
      <div class="text-3xl font-bold text-gray-900 dark:text-white mb-1">
        {{ formatCurrency(data.totalMonthlyCost) }}
      </div>
      <div class="flex items-center text-sm">
        <span :class="data.totalMonthlyCostChange >= 0 ? 'text-red-600' : 'text-green-600'" class="font-medium">
          {{ formatPercentChange(data.totalMonthlyCostChange) }}
        </span>
        <span class="text-gray-500 dark:text-gray-400 ml-1">vs last month</span>
      </div>
    </div>

    <!-- Average Daily Cost -->
    <div class="glass-panel p-6 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 hover:shadow-xl transition-shadow">
      <div class="flex justify-between items-start mb-2">
        <div class="text-gray-500 dark:text-gray-400 text-sm font-medium">Average Daily Cost</div>
        <div class="p-2 bg-blue-500/10 rounded-lg">
          <svg class="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
      </div>
      <div class="text-3xl font-bold text-gray-900 dark:text-white mb-1">
        {{ formatCurrency(data.averageDailyCost) }}
      </div>
      <div class="flex items-center text-sm">
        <span :class="getTrendColor(data.averageDailyCostTrend)" class="font-medium">
          {{ getTrendIcon(data.averageDailyCostTrend) }} {{ data.averageDailyCostTrend }}
        </span>
      </div>
    </div>

    <!-- Cost Per Query -->
    <div class="glass-panel p-6 bg-gradient-to-br from-cyan-500/10 to-teal-500/10 hover:shadow-xl transition-shadow">
      <div class="flex justify-between items-start mb-2">
        <div class="text-gray-500 dark:text-gray-400 text-sm font-medium">Cost Per Query</div>
        <div class="p-2 bg-cyan-500/10 rounded-lg">
          <svg class="w-5 h-5 text-cyan-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
          </svg>
        </div>
      </div>
      <div class="text-3xl font-bold text-gray-900 dark:text-white mb-1">
        {{ formatCurrency(data.costPerQuery) }}
      </div>
      <div class="text-sm text-gray-500 dark:text-gray-400">
        per query execution
      </div>
    </div>

    <!-- Projected Monthly Cost -->
    <div class="glass-panel p-6 bg-gradient-to-br from-teal-500/10 to-green-500/10 hover:shadow-xl transition-shadow">
      <div class="flex justify-between items-start mb-2">
        <div class="text-gray-500 dark:text-gray-400 text-sm font-medium">Projected Monthly</div>
        <div class="p-2 bg-teal-500/10 rounded-lg">
          <svg class="w-5 h-5 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        </div>
      </div>
      <div class="text-3xl font-bold text-gray-900 dark:text-white mb-1">
        {{ formatCurrency(data.projectedMonthlyCost) }}
      </div>
      <div class="text-sm text-gray-500 dark:text-gray-400">
        end of month forecast
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  data: {
    totalMonthlyCost: number
    totalMonthlyCostChange: number
    averageDailyCost: number
    averageDailyCostTrend: 'up' | 'down' | 'stable'
    costPerQuery: number
    projectedMonthlyCost: number
    sparklineData: number[]
  }
}>()

const { formatCurrency, formatPercentChange, getTrendIcon } = useCostFormatting()

const getTrendColor = (trend: string) => {
  if (trend === 'up') return 'text-red-600 dark:text-red-400'
  if (trend === 'down') return 'text-green-600 dark:text-green-400'
  return 'text-gray-600 dark:text-gray-400'
}
</script>
